// @noflow
import './calendar-days.component.css';

import {
    addDays,
    addMonths,
    firstDateInMonth,
    isDayAfter,
    isDayBefore,
    isSameDay,
    isSameMonth,
    lastDateInMonth
} from 'instadate';
import { FormattedDate, injectIntl, intlShape } from 'react-intl';

import classnames from 'classnames';
import PropTypes from 'prop-types';
import * as React from 'react';

export class CalendarDays extends React.Component {
    static propTypes = {
        dateToRender: PropTypes.object.isRequired,
        displaySingleActive: PropTypes.bool,
        endDate: PropTypes.instanceOf(Date),
        handleDateSelected: PropTypes.func.isRequired,
        intl: intlShape,
        startDate: PropTypes.instanceOf(Date)
    };

    getHeader() {
        const start = new Date(0);
        const headings = [];
        for (let dayOfWeek = 1; dayOfWeek < 8; dayOfWeek++) {
            const date = new Date(0);
            date.setDate(start.getDate() + dayOfWeek - start.getDay());
            headings.push(
                <li className={classnames('calendar-days__heading-day')} key={`day-heading-${dayOfWeek}`}>
                    <FormattedDate value={date} weekday="narrow" />
                </li>
            );
        }
        return <ul className="calendar-days__header">{headings}</ul>;
    }

    getBody() {
        const { dateToRender } = this.props;
        const lastDayOfMonth = lastDateInMonth(dateToRender);
        const daysInMonth = lastDayOfMonth.getDate();
        let firstWeekDay = firstDateInMonth(dateToRender).getDay();
        const days = [];

        if (firstWeekDay !== 1) {
            if (firstWeekDay === 0) {
                firstWeekDay = 7;
            }
            const prevMonth = addDays(firstDateInMonth(dateToRender), -1);
            const prevMonthLastDate = prevMonth.getDate();
            const prevMonthLastDay = prevMonth.getDay() === 0 ? 7 : prevMonth.getDay();
            const prevMonthLastMonday = addDays(firstDateInMonth(dateToRender), -prevMonthLastDay);

            for (let i = prevMonthLastMonday.getDate(); i < prevMonthLastDate + 1; i++) {
                const d = new Date(prevMonth);
                d.setDate(i);
                days.push(this.getDay(d));
            }
        }

        const firstDayOfMonth = firstDateInMonth(dateToRender);

        for (let i = 0; i < daysInMonth; i++) {
            const d = addDays(firstDayOfMonth, i);
            days.push(this.getDay(d));
        }

        const nextMonth = addMonths(dateToRender, 1);
        const firstDayNextMonth = firstDateInMonth(nextMonth);
        let nextMonthFirstWeekday = firstDayNextMonth.getDay();

        if (nextMonthFirstWeekday !== 1) {
            if (nextMonthFirstWeekday === 0) {
                nextMonthFirstWeekday = 7;
            }

            let index = 0;
            for (let i = nextMonthFirstWeekday; i < 8; i++) {
                const d = addDays(firstDayNextMonth, index);
                days.push(this.getDay(d));
                index++;
            }
        }

        return <ul className="calendar-days__body">{days}</ul>;
    }

    getDay(d) {
        const { displaySingleActive } = this.props;

        if (displaySingleActive === null || displaySingleActive === undefined || displaySingleActive === false) {
            return this.getDayRangeActive(d);
        } else {
            return this.getDaySingleActive(d);
        }
    }

    getDaySingleActive(d) {
        const { dateToRender, intl: { formatDate }, startDate } = this.props;
        const dString = formatDate(d, { day: '2-digit' });
        const isNotThisMonth = !isSameMonth(d, dateToRender);
        const isSameAsStart = startDate && isSameDay(d, startDate);

        return (
            <li
                className={classnames(
                    'calendar-days__day',
                    { 'calendar-days__day--active': isSameAsStart },
                    { 'calendar-days__day-content__greyed': isNotThisMonth }
                )}
                key={`calendar-day-${formatDate(d, { day: '2-digit', month: '2-digit', year: 'numeric' })}`}
                onClick={this.props.handleDateSelected.bind(this, d)}
            >
                <div
                    aria-label={`calendar-day-${formatDate(d, { day: '2-digit', month: '2-digit', year: 'numeric' })}`}
                    className={classnames('calendar-days__day-content')}
                >
                    {dString}
                </div>
                {isSameAsStart && <div className={classnames('calendar-days__day-content-active')}>{dString}</div>}
            </li>
        );
    }

    getDayRangeActive(d) {
        const { dateToRender, endDate, intl: { formatDate }, startDate } = this.props;
        const end = endDate ? endDate.getDate() : null;
        const dString = formatDate(d, { day: '2-digit' });

        const isNotThisMonth = !isSameMonth(d, dateToRender);
        const isSameAsStart = startDate && isSameDay(d, startDate);
        const isSameAsEnd = endDate && isSameDay(d, endDate);
        const isAfterStart = startDate && isDayAfter(d, startDate);
        const isBeforeEnd = endDate && isDayBefore(d, endDate);
        const currentDate = new Date();
        const isBeforeCurrentDate = isDayBefore(d, currentDate);

        return (
            <li
                className={classnames(
                    'calendar-days__day',
                    {
                        'calendar-days__day--start': isSameAsStart || isSameAsEnd
                    },
                    { 'calendar-days__day-content__greyed': isNotThisMonth },
                    {
                        'calendar-days__day-content__previous': isBeforeCurrentDate
                    }
                )}
                data-qa={isSameAsStart ? 'calendar-day-start' : isSameAsEnd ? 'calendar-day-end' : 'calendar-day'}
                key={`calendar-day-${formatDate(d, { day: '2-digit', month: '2-digit', year: 'numeric' })}`}
                onClick={this.props.handleDateSelected.bind(this, d)}
            >
                <div
                    aria-label={`day-${formatDate(d, { day: '2-digit' })}`}
                    className={classnames('calendar-days__day-content', {
                        'calendar-days__day-content-tween': isAfterStart && isBeforeEnd
                    })}
                >
                    {dString}
                </div>
                {isSameAsStart && end && <div className={classnames('calendar-days__day-content-pre')} />}
                {isSameAsStart && <div className={classnames('calendar-days__day-content-start')}>{dString}</div>}
                {isSameAsEnd && <div className={classnames('calendar-days__day-content-post')} />}
                {isSameAsEnd && <div className={classnames('calendar-days__day-content-end')}>{dString}</div>}
            </li>
        );
    }

    getFullComponent() {
        return (
            <div className="calendar-days">
                {this.getHeader()}
                {this.getBody()}
            </div>
        );
    }

    render() {
        return this.getFullComponent();
    }
}

export default injectIntl(CalendarDays);



// WEBPACK FOOTER //
// ./src/components/widgets/calendar/days/calendar-days.component.js